import React, { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FileText, 
  Search, 
  Plus, 
  Filter,
  Download,
  MoreVertical,
  TrendingUp,
  Mail,
  Eye,
  Edit,
  Trash2,
  Copy,
  Star,
  Grid3X3,
  List,
  Calendar,
  Clock,
  Target,
  Zap,
  Activity,
  BarChart3,
  DollarSign,
  Users,
  Sparkles,
  Award,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  Play,
  Pause,
  Settings,
  ChevronDown,
  ExternalLink,
  MessageSquare,
  Phone,
  Building2,
  X,
  Edit3,
  Wand2,
  Save,
  Brain,
  Variable,
  TestTube,
  Lightbulb,
  Rocket,
  ShieldCheck,
  RefreshCw,
  MousePointer,
  Upload,
  User,
  Palette
} from 'lucide-react';
import { Button } from '../../components/ui/Button/Button';
import { cn } from '../../utils/cn';
import GeminiService, { TemplateRequest } from '../../services/geminiService';
import { PersonalizationModal } from '../../components/PersonalizationModal';
import { TemplateGallery } from '../../components/TemplateGallery';
import TemplateService, { PersonalizationData, HTMLTemplate } from '../../services/templateService';

// Template interface
interface Template {
  id: string;
  name: string;
  category: 'cold-outreach' | 'follow-up' | 'meeting-request' | 'proposal' | 'thank-you';
  type: 'email' | 'linkedin' | 'sms' | 'call';
  subject?: string;
  content: string;
  tags: string[];
  openRate?: number;
  replyRate?: number;
  usage: number;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  author: string;
}

interface TemplateStats {
  totalTemplates: number;
  activeTemplates: number;
  avgOpenRate: number;
  avgReplyRate: number;
  topPerforming: number;
  totalUsage: number;
}

interface PersonalizedEmailBuilderProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (template: Template) => void;
  existingTemplate?: Template;
}

// Animated counter component
const AnimatedCounter: React.FC<{ end: number; duration?: number; suffix?: string; decimals?: number }> = ({ 
  end, 
  duration = 2000, 
  suffix = "",
  decimals = 0
}) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime: number;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      const currentCount = progress * end;
      setCount(decimals > 0 ? Number(currentCount.toFixed(decimals)) : Math.floor(currentCount));
      
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration, decimals]);
  
  return (
    <span className="font-bold">
      {decimals > 0 ? count.toFixed(decimals) : count.toLocaleString()}{suffix}
    </span>
  );
};

// Metric Card Component
const MetricCard: React.FC<{
  title: string;
  value: string | number;
  change: string;
  icon: React.ElementType;
  gradient: string;
  description?: string;
  decimals?: number;
}> = ({ title, value, change, icon: Icon, gradient, description, decimals = 0 }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4, scale: 1.02 }}
      className="group relative p-6 bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl hover:border-neutral-600/50 transition-all duration-300 shadow-xl hover:shadow-2xl"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-neutral-400 text-sm font-medium mb-2">{title}</p>
          <p className="text-3xl font-bold text-white mb-1">
            {typeof value === 'number' ? (
              <AnimatedCounter end={value} decimals={decimals} />
            ) : (
              value
            )}
          </p>
          <div className="flex items-center space-x-2">
            <span className="text-green-400 text-sm font-medium">{change}</span>
            <TrendingUp className="w-4 h-4 text-green-400" />
          </div>
          {description && (
            <p className="text-neutral-500 text-xs mt-2">{description}</p>
          )}
        </div>
        <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center", gradient)}>
          <Icon className="w-6 h-6 text-white" />
              </div>
            </div>
            
      {/* Hover effect overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    </motion.div>
  );
};

// Template Card Component
const TemplateCard: React.FC<{
  template: Template;
  onView: (template: Template) => void;
  onEdit: (template: Template) => void;
  onDelete: (template: Template) => void;
  onClone: (template: Template) => void;
}> = ({ template, onView, onEdit, onDelete, onClone }) => {
  const [showActions, setShowActions] = useState(false);

  const getCategoryConfig = (category: string) => {
    switch (category) {
      case 'cold-outreach':
        return { color: 'text-blue-400', bg: 'bg-blue-500/20', label: 'Cold Outreach' };
      case 'follow-up':
        return { color: 'text-green-400', bg: 'bg-green-500/20', label: 'Follow-up' };
      case 'meeting-request':
        return { color: 'text-purple-400', bg: 'bg-purple-500/20', label: 'Meeting Request' };
      case 'proposal':
        return { color: 'text-orange-400', bg: 'bg-orange-500/20', label: 'Proposal' };
      default:
        return { color: 'text-pink-400', bg: 'bg-pink-500/20', label: 'Thank You' };
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'email': return <Mail className="w-5 h-5" />;
      case 'sms': return <MessageSquare className="w-5 h-5" />;
      case 'linkedin': return <Building2 className="w-5 h-5" />;
      case 'call': return <Phone className="w-5 h-5" />;
      default: return <FileText className="w-5 h-5" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'email': return 'text-blue-500 bg-blue-500/20';
      case 'sms': return 'text-green-500 bg-green-500/20';
      case 'linkedin': return 'text-purple-500 bg-purple-500/20';
      case 'call': return 'text-orange-500 bg-orange-500/20';
      default: return 'text-neutral-500 bg-neutral-500/20';
    }
  };

  const categoryConfig = getCategoryConfig(template.category);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -2, scale: 1.01 }}
      className="group relative p-6 bg-gradient-to-br from-neutral-900/60 to-neutral-800/40 backdrop-blur-xl border border-neutral-700/40 rounded-2xl hover:border-neutral-600/50 transition-all duration-300 shadow-lg hover:shadow-xl"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
            <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 mb-2">
            <div className={cn(
              "w-8 h-8 rounded-lg flex items-center justify-center",
              getTypeColor(template.type)
            )}>
              {getTypeIcon(template.type)}
            </div>
            <h3 className="text-lg font-semibold text-white truncate">
                  {template.name}
                </h3>
              </div>
          
          {template.subject && (
            <p className="text-neutral-400 text-sm mb-2 line-clamp-1">
              Subject: {template.subject}
            </p>
          )}
          
          <div className="flex items-center space-x-2 mb-3">
            <div className={cn(
              "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium",
              categoryConfig.color,
              categoryConfig.bg
            )}>
              {categoryConfig.label}
                </div>
            <div className={cn(
              "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border",
              template.isActive 
                ? "text-green-400 bg-green-500/20 border-green-500/30"
                : "text-neutral-400 bg-neutral-500/20 border-neutral-500/30"
            )}>
              {template.isActive ? 'Active' : 'Inactive'}
                </div>
              </div>
            </div>
        
        <div className="relative">
          <button
            onClick={() => setShowActions(!showActions)}
            className="p-2 text-neutral-400 hover:text-white hover:bg-neutral-800/50 rounded-lg transition-colors"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
          
          <AnimatePresence>
            {showActions && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                className="absolute right-0 top-full mt-2 w-48 bg-neutral-900/95 backdrop-blur-xl border border-neutral-700 rounded-xl shadow-2xl z-10"
              >
                <div className="p-2">
                  <button
                    onClick={() => onView(template)}
                    className="w-full flex items-center space-x-3 px-3 py-2 text-neutral-300 hover:text-white hover:bg-neutral-800/50 rounded-lg transition-colors text-left"
                  >
                    <Eye className="w-4 h-4" />
                    <span className="text-sm">View Template</span>
                  </button>
                  <button
                    onClick={() => onEdit(template)}
                    className="w-full flex items-center space-x-3 px-3 py-2 text-neutral-300 hover:text-white hover:bg-neutral-800/50 rounded-lg transition-colors text-left"
                  >
                    <Edit className="w-4 h-4" />
                    <span className="text-sm">Edit</span>
                  </button>
                  <button
                    onClick={() => onClone(template)}
                    className="w-full flex items-center space-x-3 px-3 py-2 text-neutral-300 hover:text-white hover:bg-neutral-800/50 rounded-lg transition-colors text-left"
                  >
                    <Copy className="w-4 h-4" />
                    <span className="text-sm">Clone</span>
                  </button>
                  <div className="border-t border-neutral-700 my-1" />
                  <button
                    onClick={() => onDelete(template)}
                    className="w-full flex items-center space-x-3 px-3 py-2 text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors text-left"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span className="text-sm">Delete</span>
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Content Preview */}
      <div className="mb-4 p-3 bg-neutral-800/30 rounded-lg">
        <p className="text-neutral-300 text-sm line-clamp-3">
          {template.content}
        </p>
          </div>
          
          {/* Performance Metrics */}
      <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="text-center">
          <p className="text-lg font-semibold text-blue-400">
            {template.usage}
          </p>
          <p className="text-xs text-neutral-400">Times Used</p>
              </div>
        {template.openRate && (
            <div className="text-center">
            <p className="text-lg font-semibold text-green-400">
              {template.openRate}%
            </p>
            <p className="text-xs text-neutral-400">Open Rate</p>
              </div>
        )}
        {template.replyRate && (
            <div className="text-center">
            <p className="text-lg font-semibold text-purple-400">
              {template.replyRate}%
            </p>
            <p className="text-xs text-neutral-400">Reply Rate</p>
              </div>
        )}
          </div>
          
      {/* Tags */}
      {template.tags.length > 0 && (
        <div className="mb-4">
          <div className="flex flex-wrap gap-1">
            {template.tags.slice(0, 3).map((tag, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-neutral-700/50 text-neutral-300 text-xs rounded-full"
              >
                {tag}
              </span>
            ))}
            {template.tags.length > 3 && (
              <span className="px-2 py-1 bg-neutral-700/50 text-neutral-400 text-xs rounded-full">
                +{template.tags.length - 3} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-neutral-500">
        <span>By {template.author}</span>
        <span>{new Date(template.updatedAt).toLocaleDateString()}</span>
      </div>
      
      {/* Hover effect overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    </motion.div>
  );
};

// Personalized Email Builder Modal Component
const PersonalizedEmailBuilder: React.FC<PersonalizedEmailBuilderProps> = ({ 
  isOpen, 
  onClose, 
  onSave, 
  existingTemplate 
}) => {
  const [template, setTemplate] = useState<Partial<Template>>({
    name: existingTemplate?.name || '',
    subject: existingTemplate?.subject || '',
    content: existingTemplate?.content || '',
    category: existingTemplate?.category || 'cold-outreach',
    type: 'email',
    tags: existingTemplate?.tags || [],
    isActive: existingTemplate?.isActive ?? true,
    ...(existingTemplate || {})
  });

  const [activeTab, setActiveTab] = useState<'builder' | 'ai' | 'preview' | 'analytics'>('builder');
  const [isGenerating, setIsGenerating] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [personalizationLevel, setPersonalizationLevel] = useState<'basic' | 'advanced' | 'ai-powered'>('ai-powered');
  const [targetRole, setTargetRole] = useState('CEO');
  const [industry, setIndustry] = useState('Technology');
  const [companySize, setCompanySize] = useState('50-200 employees');
  const [tone, setTone] = useState('professional');
  const [urgency, setUrgency] = useState('medium');
  const [includePersonalization, setIncludePersonalization] = useState(true);
  const [includeAB, setIncludeAB] = useState(false);
  const [trackingPixel, setTrackingPixel] = useState(true);
  const [smartSending, setSmartSending] = useState(true);

  const personalizationVariables = [
    '{{firstName}}', '{{lastName}}', '{{company}}', '{{title}}', 
    '{{industry}}', '{{location}}', '{{website}}', '{{phone}}',
    '{{recentNews}}', '{{mutualConnection}}', '{{companySize}}', '{{revenue}}'
  ];

  const generateWithAI = async () => {
    if (!aiPrompt.trim()) return;

    setIsGenerating(true);
    try {
      // Simulate AI generation (replace with actual Gemini API call)
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const generatedTemplate = {
        name: 'AI-Generated Email Template',
        subject: '{{firstName}}, quick question about {{company}}\'s growth strategy',
        content: `Hi {{firstName}},

I noticed {{company}} has been expanding rapidly in the {{industry}} space, particularly with your recent {{recentNews}}.

We've helped similar companies like yours reduce their operational costs by 35% while scaling their {{department}} teams. Given your role as {{title}}, I thought this might be relevant.

Would you be open to a brief 15-minute conversation next week to discuss how we could support {{company}}'s continued growth?

Best regards,
[Your Name]

P.S. I saw your post about {{recentPost}} on LinkedIn - great insights!`,
        category: template.category,
        tags: ['ai-generated', 'personalized', 'growth-focused']
      };

      setTemplate(prev => ({
        ...prev,
        ...generatedTemplate
      }));
    } catch (error) {
      console.error('AI generation failed:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const insertVariable = (variable: string) => {
    const textarea = document.getElementById('email-content') as HTMLTextAreaElement;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const currentContent = template.content || '';
      const newContent = currentContent.substring(0, start) + variable + currentContent.substring(end);
      
      setTemplate(prev => ({ ...prev, content: newContent }));
      
      // Restore cursor position
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variable.length, start + variable.length);
      }, 0);
    }
  };

  const handleSave = () => {
    const finalTemplate: Template = {
      id: existingTemplate?.id || `tpl-${Date.now()}`,
      name: template.name || 'Untitled Template',
      subject: template.subject || '',
      content: template.content || '',
      category: template.category || 'cold-outreach',
      type: 'email',
      tags: template.tags || [],
      openRate: existingTemplate?.openRate,
      replyRate: existingTemplate?.replyRate,
      usage: existingTemplate?.usage || 0,
      createdAt: existingTemplate?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isActive: template.isActive ?? true,
      author: existingTemplate?.author || 'Current User'
    };

    onSave(finalTemplate);
    onClose();
  };

  const previewContent = template.content
    ?.replace(/\{\{firstName\}\}/g, 'John')
    ?.replace(/\{\{lastName\}\}/g, 'Smith')
    ?.replace(/\{\{company\}\}/g, 'TechCorp Inc.')
    ?.replace(/\{\{title\}\}/g, 'CEO')
    ?.replace(/\{\{industry\}\}/g, 'Technology')
    ?.replace(/\{\{location\}\}/g, 'San Francisco')
    ?.replace(/\{\{recentNews\}\}/g, 'Series B funding announcement')
    ?.replace(/\{\{mutualConnection\}\}/g, 'Sarah Johnson')
    ?.replace(/\{\{companySize\}\}/g, '150 employees')
    ?.replace(/\{\{recentPost\}\}/g, 'AI transformation strategies');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-gradient-to-br from-neutral-900/95 to-neutral-800/95 backdrop-blur-xl border border-neutral-700/50 rounded-2xl w-full max-w-7xl max-h-[95vh] overflow-hidden shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-700/50">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
              <Brain className="w-5 h-5 text-white" />
            </div>
                <div>
              <h2 className="text-xl font-bold text-white">
                Personalized Email Builder
              </h2>
              <p className="text-neutral-400 text-sm">Create AI-powered, highly personalized email templates</p>
                  </div>
                </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-purple-700 transition-colors"
            >
              <Save className="w-4 h-4 mr-2 inline" />
              Save Template
            </button>
            <button
              onClick={onClose}
              className="p-2 text-neutral-400 hover:text-white hover:bg-neutral-700/50 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center space-x-1 bg-neutral-800/50 rounded-xl p-1 m-6 mb-0">
          {[
            { id: 'builder', label: 'Builder', icon: Edit3 },
            { id: 'ai', label: 'AI Assistant', icon: Brain },
            { id: 'preview', label: 'Preview', icon: Eye },
            { id: 'analytics', label: 'Analytics', icon: BarChart3 }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={cn(
                'flex items-center space-x-2 px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200',
                activeTab === tab.id
                  ? 'bg-blue-500 text-white shadow-lg'
                  : 'text-neutral-400 hover:text-white hover:bg-neutral-700/50'
              )}
            >
              <tab.icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        <div className="flex h-[calc(95vh-140px)]">
          {/* Left Sidebar - Always visible */}
          <div className="w-80 bg-neutral-800/30 border-r border-neutral-700/50 p-6 overflow-y-auto">
            <div className="space-y-6">
              {/* Basic Settings */}
              <div>
                <h3 className="text-sm font-medium text-neutral-300 mb-3">Basic Settings</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-neutral-400 mb-2">Template Name</label>
                    <input
                      value={template.name}
                      onChange={(e) => setTemplate(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Enter template name..."
                      className="w-full px-3 py-2 bg-neutral-800/50 border border-neutral-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-neutral-400 mb-2">Category</label>
                    <select
                      value={template.category}
                      onChange={(e) => setTemplate(prev => ({ ...prev, category: e.target.value as 'cold-outreach' | 'follow-up' | 'meeting-request' | 'proposal' | 'thank-you' }))}
                      className="w-full px-3 py-2 bg-neutral-800/50 border border-neutral-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                    >
                      <option value="cold-outreach">Cold Outreach</option>
                      <option value="follow-up">Follow-up</option>
                      <option value="meeting-request">Meeting Request</option>
                      <option value="proposal">Proposal</option>
                      <option value="thank-you">Thank You</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Targeting */}
              <div>
                <h3 className="text-sm font-medium text-neutral-300 mb-3">Target Audience</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-neutral-400 mb-1">Role</label>
                    <select
                      value={targetRole}
                      onChange={(e) => setTargetRole(e.target.value)}
                      className="w-full px-3 py-2 bg-neutral-800/50 border border-neutral-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                    >
                      <option value="CEO">CEO</option>
                      <option value="CTO">CTO</option>
                      <option value="VP Sales">VP Sales</option>
                      <option value="Marketing Director">Marketing Director</option>
                      <option value="Operations Manager">Operations Manager</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-neutral-400 mb-1">Industry</label>
                    <select
                      value={industry}
                      onChange={(e) => setIndustry(e.target.value)}
                      className="w-full px-3 py-2 bg-neutral-800/50 border border-neutral-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                    >
                      <option value="Technology">Technology</option>
                      <option value="Healthcare">Healthcare</option>
                      <option value="Finance">Finance</option>
                      <option value="Manufacturing">Manufacturing</option>
                      <option value="Retail">Retail</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-neutral-400 mb-1">Company Size</label>
                    <select
                      value={companySize}
                      onChange={(e) => setCompanySize(e.target.value)}
                      className="w-full px-3 py-2 bg-neutral-800/50 border border-neutral-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                    >
                      <option value="1-50 employees">1-50 employees</option>
                      <option value="50-200 employees">50-200 employees</option>
                      <option value="200-1000 employees">200-1000 employees</option>
                      <option value="1000+ employees">1000+ employees</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Personalization Variables */}
              <div>
                <h3 className="text-sm font-medium text-neutral-300 mb-3 flex items-center">
                  <Variable className="w-4 h-4 mr-2" />
                  Variables
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {personalizationVariables.map((variable) => (
                    <button
                      key={variable}
                      onClick={() => insertVariable(variable)}
                      className="px-2 py-1 bg-neutral-700/50 hover:bg-neutral-700 border border-neutral-600/50 rounded text-xs text-neutral-300 hover:text-white transition-colors"
                    >
                      {variable}
                    </button>
                  ))}
                </div>
              </div>

              {/* Advanced Settings */}
              <div>
                <h3 className="text-sm font-medium text-neutral-300 mb-3">Advanced Settings</h3>
                <div className="space-y-3">
                  <label className="flex items-center space-x-2">
                    <input 
                      type="checkbox" 
                      checked={includePersonalization}
                      onChange={(e) => setIncludePersonalization(e.target.checked)}
                      className="rounded border-neutral-600 bg-neutral-800 text-blue-500" 
                    />
                    <span className="text-xs text-neutral-400">Smart personalization</span>
                </label>
                  
                  <label className="flex items-center space-x-2">
                    <input 
                      type="checkbox" 
                      checked={includeAB}
                      onChange={(e) => setIncludeAB(e.target.checked)}
                      className="rounded border-neutral-600 bg-neutral-800 text-blue-500" 
                    />
                    <span className="text-xs text-neutral-400">A/B test ready</span>
                  </label>
                  
                  <label className="flex items-center space-x-2">
                    <input 
                      type="checkbox" 
                      checked={trackingPixel}
                      onChange={(e) => setTrackingPixel(e.target.checked)}
                      className="rounded border-neutral-600 bg-neutral-800 text-blue-500" 
                    />
                    <span className="text-xs text-neutral-400">Include tracking</span>
                  </label>
                  
                  <label className="flex items-center space-x-2">
                    <input 
                      type="checkbox" 
                      checked={smartSending}
                      onChange={(e) => setSmartSending(e.target.checked)}
                      className="rounded border-neutral-600 bg-neutral-800 text-blue-500" 
                    />
                    <span className="text-xs text-neutral-400">Smart send time</span>
                  </label>
                </div>
              </div>
                </div>
              </div>
              
          {/* Main Content Area */}
          <div className="flex-1 p-6 overflow-y-auto">
            {activeTab === 'builder' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-3">Subject Line</label>
                  <input
                    value={template.subject}
                    onChange={(e) => setTemplate(prev => ({ ...prev, subject: e.target.value }))}
                    placeholder="Enter compelling subject line..."
                    className="w-full px-4 py-3 bg-neutral-800/50 border border-neutral-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  />
                  <div className="mt-2 flex items-center justify-between text-xs">
                    <span className="text-neutral-400">
                      Length: {template.subject?.length || 0}/50 characters
                      </span>
                    <div className="flex items-center space-x-2 text-neutral-400">
                      <ShieldCheck className="w-3 h-3" />
                      <span>Spam score: Low</span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-neutral-300 mb-3">Email Content</label>
                  <textarea
                    id="email-content"
                    value={template.content}
                    onChange={(e) => setTemplate(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="Write your email content here... Use variables like {{firstName}} for personalization."
                    rows={16}
                    className="w-full px-4 py-3 bg-neutral-800/50 border border-neutral-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 resize-none font-mono text-sm"
                  />
                  <div className="mt-2 text-xs text-neutral-400">
                    Characters: {template.content?.length || 0} | 
                    Variables: {(template.content?.match(/\{\{[^}]+\}\}/g) || []).length}
                </div>
              </div>
            </div>
            )}

            {activeTab === 'ai' && (
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-xl p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                      <Brain className="w-4 h-4 text-white" />
    </div>
                    <h3 className="text-lg font-semibold text-white">AI Email Generator</h3>
                  </div>
                  
                  <div className="space-y-4">
                    <textarea
                      value={aiPrompt}
                      onChange={(e) => setAiPrompt(e.target.value)}
                      placeholder="Describe the email you want to create. For example: 'Create a follow-up email for prospects who attended our webinar about AI automation. Focus on the ROI benefits and include a demo booking CTA.'"
                      rows={4}
                      className="w-full px-4 py-3 bg-neutral-800/50 border border-neutral-700 rounded-xl text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50 resize-none"
                    />
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-neutral-300 mb-2">Tone</label>
                        <select
                          value={tone}
                          onChange={(e) => setTone(e.target.value)}
                          className="w-full px-3 py-2 bg-neutral-800/50 border border-neutral-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                        >
                          <option value="professional">Professional</option>
                          <option value="friendly">Friendly</option>
                          <option value="casual">Casual</option>
                          <option value="authoritative">Authoritative</option>
                          <option value="consultative">Consultative</option>
                        </select>
            </div>
            
                      <div>
                        <label className="block text-sm font-medium text-neutral-300 mb-2">Urgency</label>
                        <select
                          value={urgency}
                          onChange={(e) => setUrgency(e.target.value)}
                          className="w-full px-3 py-2 bg-neutral-800/50 border border-neutral-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-neutral-300 mb-2">Personalization</label>
                        <select
                          value={personalizationLevel}
                          onChange={(e) => setPersonalizationLevel(e.target.value as any)}
                          className="w-full px-3 py-2 bg-neutral-800/50 border border-neutral-700 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                        >
                          <option value="basic">Basic</option>
                          <option value="advanced">Advanced</option>
                          <option value="ai-powered">AI-Powered</option>
                        </select>
                      </div>
                    </div>
                    
                    <button
                      onClick={generateWithAI}
                      disabled={!aiPrompt.trim() || isGenerating}
                      className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-3 px-6 rounded-xl transition-all duration-200"
                    >
                      {isGenerating ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin inline" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Wand2 className="w-4 h-4 mr-2 inline" />
                          Generate Email Template
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Quick Templates */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <Lightbulb className="w-5 h-5 mr-2" />
                    Quick Templates
                </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[
                      { name: 'Cold Outreach', desc: 'First contact with value proposition', prompt: 'Create a cold outreach email for B2B SaaS prospects. Focus on specific pain points and offer a clear value proposition.' },
                      { name: 'Follow-up', desc: 'Second touchpoint for non-responders', prompt: 'Create a follow-up email for prospects who didn\'t respond to the first outreach. Add social proof and case study.' },
                      { name: 'Meeting Request', desc: 'Direct scheduling with calendar link', prompt: 'Create a meeting request email for qualified prospects. Include specific agenda and value they\'ll get from the meeting.' },
                      { name: 'Demo Follow-up', desc: 'Post-demo next steps', prompt: 'Create a follow-up email after a product demo. Address specific objections and provide next steps.' }
                    ].map((quickTemplate, index) => (
                      <button
                        key={index}
                        onClick={() => setAiPrompt(quickTemplate.prompt)}
                        className="p-4 bg-neutral-800/30 hover:bg-neutral-800/50 border border-neutral-700/50 rounded-lg text-left transition-colors group"
                      >
                        <div className="text-white font-medium text-sm mb-1 group-hover:text-blue-400 transition-colors">
                          {quickTemplate.name}
              </div>
                        <div className="text-neutral-400 text-xs">{quickTemplate.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'preview' && (
              <div className="space-y-6">
                <div className="bg-white rounded-xl p-6 text-black">
                  <div className="border-b border-gray-200 pb-4 mb-4">
                    <div className="text-sm text-gray-600">Subject:</div>
                    <div className="font-semibold text-lg">
                      {template.subject?.replace(/\{\{firstName\}\}/g, 'John').replace(/\{\{company\}\}/g, 'TechCorp Inc.') || 'No subject line'}
            </div>
          </div>
          
                  <div className="prose prose-sm max-w-none">
                    <div style={{ whiteSpace: 'pre-wrap', fontFamily: 'system-ui' }}>
                      {previewContent || 'No content to preview'}
                    </div>
                  </div>
                </div>

                <div className="bg-neutral-800/30 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <TestTube className="w-5 h-5 mr-2" />
                    Preview Settings
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-neutral-400">Preview Name:</span>
                      <span className="text-white ml-2">John Smith</span>
                    </div>
                    <div>
                      <span className="text-neutral-400">Company:</span>
                      <span className="text-white ml-2">TechCorp Inc.</span>
                    </div>
                    <div>
                      <span className="text-neutral-400">Title:</span>
                      <span className="text-white ml-2">CEO</span>
                    </div>
                    <div>
                      <span className="text-neutral-400">Industry:</span>
                      <span className="text-white ml-2">Technology</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'analytics' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-neutral-800/30 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-white">Predicted Performance</h3>
                      <Rocket className="w-5 h-5 text-blue-400" />
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-neutral-400">Open Rate:</span>
                        <span className="text-green-400 font-medium">28.5%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-neutral-400">Click Rate:</span>
                        <span className="text-blue-400 font-medium">4.2%</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-neutral-400">Reply Rate:</span>
                        <span className="text-purple-400 font-medium">2.8%</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-neutral-800/30 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-white">Personalization Score</h3>
                      <Star className="w-5 h-5 text-yellow-400" />
                    </div>
            <div className="text-center">
                      <div className="text-3xl font-bold text-yellow-400 mb-2">87%</div>
                      <div className="text-sm text-neutral-400">Highly Personalized</div>
              </div>
                  </div>

                  <div className="bg-neutral-800/30 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-white">Deliverability</h3>
                      <ShieldCheck className="w-5 h-5 text-green-400" />
            </div>
            <div className="text-center">
                      <div className="text-3xl font-bold text-green-400 mb-2">95%</div>
                      <div className="text-sm text-neutral-400">Excellent Score</div>
              </div>
            </div>
          </div>
          
                <div className="bg-neutral-800/30 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <Lightbulb className="w-5 h-5 mr-2" />
                    Optimization Suggestions
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-blue-400 mt-0.5" />
                      <div>
                        <div className="text-white font-medium text-sm">Great personalization!</div>
                        <div className="text-neutral-400 text-xs">Your use of variables will increase engagement</div>
        </div>
      </div>
      
                    <div className="flex items-start space-x-3 p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                      <AlertCircle className="w-5 h-5 text-orange-400 mt-0.5" />
                      <div>
                        <div className="text-white font-medium text-sm">Consider A/B testing subject line</div>
                        <div className="text-neutral-400 text-xs">Try a shorter version to improve open rates</div>
                  </div>
                </div>
                
                    <div className="flex items-start space-x-3 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                      <div>
                        <div className="text-white font-medium text-sm">Clear call-to-action</div>
                        <div className="text-neutral-400 text-xs">Your CTA is specific and actionable</div>
                </div>
              </div>
            </div>
                </div>
              </div>
        )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export const TemplatesPage: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [activeTab, setActiveTab] = useState<'email' | 'sms' | 'linkedin' | 'call'>('email');
  const [emailSubTab, setEmailSubTab] = useState<'regular' | 'prebuilt'>('prebuilt');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('All');
  const [showEmailBuilder, setShowEmailBuilder] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | undefined>(undefined);
  const [showPersonalizationModal, setShowPersonalizationModal] = useState(false);
  const [showTemplateGallery, setShowTemplateGallery] = useState(false);
  const [recipientData, setRecipientData] = useState<PersonalizationData | undefined>();
  const [beautifulTemplates, setBeautifulTemplates] = useState<HTMLTemplate[]>([]);
  const [selectedTemplatePreview, setSelectedTemplatePreview] = useState<string>('');
  const [previewTemplate, setPreviewTemplate] = useState<HTMLTemplate | null>(null);
  const [stats, setStats] = useState<TemplateStats>({
    totalTemplates: 0,
    activeTemplates: 0,
    avgOpenRate: 0,
    avgReplyRate: 0,
    topPerforming: 0,
    totalUsage: 0
  });

  const loadTemplates = async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const mockTemplates: Template[] = [
        // Email templates
        {
          id: 'tpl-001',
          name: 'Cold Outreach - SaaS',
          category: 'cold-outreach',
          type: 'email',
          subject: 'Quick question about {{company}} growth',
          content: 'Hi {{firstName}}, I noticed {{company}} has been expanding rapidly. I wanted to reach out because we help similar companies...',
          tags: ['saas', 'cold', 'growth'],
          openRate: 24.5,
          replyRate: 8.2,
          usage: 156,
          createdAt: '2024-01-15',
          updatedAt: '2024-01-20',
          isActive: true,
          author: 'Sales Team'
        },
        {
          id: 'tpl-002',
          name: 'Follow-up Email',
          category: 'follow-up',
          type: 'email',
          subject: 'Following up on our conversation',
          content: 'Hi {{firstName}}, Thanks for taking the time to speak with me yesterday about {{topic}}...',
          tags: ['follow-up', 'conversation'],
          openRate: 45.2,
          replyRate: 15.8,
          usage: 89,
          createdAt: '2024-01-18',
          updatedAt: '2024-01-22',
          isActive: true,
          author: 'Sales Team'
        },
        // LinkedIn templates
        {
          id: 'tpl-003',
          name: 'LinkedIn Connection Request',
          category: 'cold-outreach',
          type: 'linkedin',
          content: 'Hi {{firstName}}, I noticed you\'re the {{title}} at {{company}}. I\'d love to connect and share some insights about {{industry}} trends.',
          tags: ['linkedin', 'connection', 'industry'],
          usage: 234,
          createdAt: '2024-01-10',
          updatedAt: '2024-01-25',
          isActive: true,
          author: 'Marketing Team'
        },
        // SMS templates
        {
          id: 'tpl-004',
          name: 'SMS Follow-up',
          category: 'follow-up',
          type: 'sms',
          content: 'Hi {{firstName}}, this is {{senderName}} from {{company}}. Just wanted to follow up on our email conversation. Are you available for a quick call?',
          tags: ['sms', 'follow-up', 'urgent'],
          usage: 67,
          createdAt: '2024-01-12',
          updatedAt: '2024-01-28',
          isActive: true,
          author: 'Sales Team'
        },
        // Call templates
        {
          id: 'tpl-005',
          name: 'Cold Call Script',
          category: 'cold-outreach',
          type: 'call',
          content: 'Hi {{firstName}}, this is {{senderName}} from {{company}}. I\'m calling because we help companies like {{company}} increase their {{metric}} by {{percentage}}%. Do you have 30 seconds for me to explain how?',
          tags: ['cold-call', 'script', 'value-prop'],
          usage: 123,
          createdAt: '2024-01-14',
          updatedAt: '2024-01-26',
          isActive: true,
          author: 'Sales Team'
        }
      ];
      
      setTemplates(mockTemplates);
    } catch (error) {
      console.error('Failed to load templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      // Simulate API call
      const mockStats: TemplateStats = {
        totalTemplates: 15,
        activeTemplates: 12,
        avgOpenRate: 28.7,
        avgReplyRate: 9.4,
        topPerforming: 3,
        totalUsage: 1247
      };
      setStats(mockStats);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  useEffect(() => {
    loadTemplates();
    loadStats();
    loadBeautifulTemplates();
  }, []);

  const loadBeautifulTemplates = async () => {
    try {
      const templates = TemplateService.getTemplates();
      console.log('Beautiful templates loaded:', templates);
      setBeautifulTemplates(templates);
    } catch (error) {
      console.error('Failed to load beautiful templates:', error);
    }
  };

  const handlePersonalizationSave = (data: PersonalizationData) => {
    setRecipientData(data);
    setShowPersonalizationModal(false);
    setShowTemplateGallery(true);
  };

  const handleBeautifulTemplateSelection = (template: HTMLTemplate, personalizedHTML?: string) => {
    // Convert HTMLTemplate to regular Template format for display
    const convertedTemplate: Template = {
      id: template.id,
      name: template.name,
      category: template.category as any,
      type: 'email',
      subject: personalizedHTML ? extractSubjectFromHTML(personalizedHTML) : `Beautiful ${template.name}`,
      content: personalizedHTML || template.description,
      tags: template.features,
      openRate: 28.5,
      replyRate: 8.2,
      usage: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isActive: true,
      author: 'Beautiful Templates'
    };

    // Add to templates list or handle as needed
    setTemplates(prev => [...prev, convertedTemplate]);
    setShowTemplateGallery(false);
  };

  const extractSubjectFromHTML = (html: string): string => {
    const titleMatch = html.match(/<title>(.*?)<\/title>/i);
    if (titleMatch) return titleMatch[1];
    
    const h1Match = html.match(/<h1[^>]*>(.*?)<\/h1>/i);
    if (h1Match) return h1Match[1].replace(/<[^>]*>/g, '');
    
    return 'Personalized Email';
  };

  const handlePreviewBeautifulTemplate = async (template: HTMLTemplate) => {
    try {
      setPreviewTemplate(template);
      const previewHTML = await TemplateService.generateTemplatePreview(
        template.id,
        recipientData || {
          firstName: 'John',
          lastName: 'Smith',
          email: 'john.smith@company.com',
          company: 'TechCorp Inc.',
          title: 'CEO',
          industry: 'Technology',
          location: 'San Francisco, CA'
        }
      );
      setSelectedTemplatePreview(previewHTML);
    } catch (error) {
      console.error('Failed to generate preview:', error);
    }
  };

  const handleView = (template: Template) => {
    console.log('View template:', template.name);
  };

  const handleEdit = (template: Template) => {
    setEditingTemplate(template);
    setShowEmailBuilder(true);
  };

  const handleDelete = (template: Template) => {
    console.log('Delete template:', template.name);
  };

  const handleClone = (template: Template) => {
    console.log('Clone template:', template.name);
  };

  const handleCreateTemplate = () => {
    setEditingTemplate(undefined);
    setShowEmailBuilder(true);
  };

  const handleSaveTemplate = (newTemplate: Template) => {
    if (editingTemplate) {
      setTemplates(prev => prev.map(t => t.id === newTemplate.id ? newTemplate : t));
    } else {
      setTemplates(prev => [...prev, newTemplate]);
    }
    setShowEmailBuilder(false);
    setEditingTemplate(undefined);
  };

  // Filter templates by active tab and other filters
  const filteredTemplates = templates.filter(template => {
    const matchesType = template.type === activeTab;
    const matchesSubTab = activeTab !== 'email' || emailSubTab === 'regular';
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         template.content.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'All' || template.category === categoryFilter.toLowerCase().replace(' ', '-');
    
    return matchesType && matchesSubTab && matchesSearch && matchesCategory;
  });

  const beautifulTemplatesForDisplay = activeTab === 'email' && emailSubTab === 'prebuilt' ? beautifulTemplates : [];

  console.log('Template display debug:', {
    activeTab,
    emailSubTab,
    beautifulTemplatesCount: beautifulTemplates.length,
    beautifulTemplatesForDisplayCount: beautifulTemplatesForDisplay.length,
    filteredTemplatesCount: filteredTemplates.length
  });

  const tabs = [
    { id: 'email', label: 'Email', icon: Mail, count: templates.filter(t => t.type === 'email').length },
    { id: 'sms', label: 'SMS', icon: MessageSquare, count: templates.filter(t => t.type === 'sms').length },
    { id: 'linkedin', label: 'LinkedIn', icon: Building2, count: templates.filter(t => t.type === 'linkedin').length },
    { id: 'call', label: 'Call Scripts', icon: Phone, count: templates.filter(t => t.type === 'call').length }
  ];

  const openEmailBuilder = (template?: Template) => {
    setEditingTemplate(template);
    setShowEmailBuilder(true);
  };

  const handleTemplateFromBuilder = (template: Template) => {
    if (editingTemplate) {
      // Update existing template
      setTemplates(prev => prev.map(t => t.id === template.id ? template : t));
    } else {
      // Add new template
      setTemplates(prev => [...prev, template]);
    }
    setShowEmailBuilder(false);
    setEditingTemplate(undefined);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-64 h-64 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-80 h-80 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
                <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent mb-2">
              Templates
            </h1>
            <p className="text-xl text-neutral-400">
              Create and manage templates for all your outreach channels
            </p>
                </div>
            <div className="flex items-center space-x-3">
            <Button 
              variant="outline"
              size="lg"
              className="border-neutral-600 text-neutral-300 hover:bg-neutral-800"
            >
              <BarChart3 className="w-5 h-5 mr-2" />
              Analytics
            </Button>
            <Button 
              variant="outline"
              size="lg"
              className="border-neutral-600 text-neutral-300 hover:bg-neutral-800"
            >
              <Download className="w-5 h-5 mr-2" />
              Export
            </Button>
            <Button 
              size="lg"
              onClick={() => openEmailBuilder()}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300"
            >
              <Brain className="w-5 h-5 mr-2" />
              AI Email Builder
            </Button>
                </div>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          <MetricCard
            title="Total Templates"
            value={stats.totalTemplates}
            change="+5 this month"
            icon={FileText}
            gradient="bg-gradient-to-r from-blue-500 to-cyan-500"
            description="All template types"
          />
          <MetricCard
            title="Active Templates"
            value={stats.activeTemplates}
            change="+2 vs last month"
            icon={CheckCircle}
            gradient="bg-gradient-to-r from-green-500 to-emerald-500"
            description="Currently in use"
          />
          <MetricCard
            title="Avg Open Rate"
            value={`${stats.avgOpenRate}%`}
            change="+3.2% improvement"
            icon={Eye}
            gradient="bg-gradient-to-r from-purple-500 to-pink-500"
            description="Email templates only"
            decimals={1}
          />
          <MetricCard
            title="Total Usage"
            value={stats.totalUsage.toLocaleString()}
            change="+15% this quarter"
            icon={Target}
            gradient="bg-gradient-to-r from-orange-500 to-red-500"
            description="Times templates used"
          />
        </motion.div>

        {/* Template Type Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl p-6 mb-8 shadow-xl"
        >
          <div className="flex items-center space-x-1 bg-neutral-800/50 rounded-xl p-1 mb-6">
            {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={cn(
                  'flex items-center space-x-2 px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200',
                    activeTab === tab.id
                    ? 'bg-blue-500 text-white shadow-lg'
                    : 'text-neutral-400 hover:text-white hover:bg-neutral-700/50'
                  )}
                >
                  <tab.icon className="w-4 h-4" />
                  <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Email Sub-tabs */}
          {activeTab === 'email' && (
            <div className="flex items-center space-x-1 bg-neutral-800/30 rounded-xl p-1 mb-6">
              <button
                onClick={() => setEmailSubTab('prebuilt')}
                className={cn(
                  'flex items-center space-x-2 px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200',
                  emailSubTab === 'prebuilt'
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                    : 'text-neutral-400 hover:text-white hover:bg-neutral-700/50'
                )}
              >
                <Sparkles className="w-4 h-4" />
                <span>Beautiful Templates</span>
                <span className="ml-2 text-xs bg-gradient-to-r from-purple-600 to-pink-600 text-white px-2 py-1 rounded-full">
                  {beautifulTemplates.length}
                    </span>
              </button>
              <button
                onClick={() => setEmailSubTab('regular')}
                className={cn(
                  'flex items-center space-x-2 px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200',
                  emailSubTab === 'regular'
                    ? 'bg-blue-500 text-white shadow-lg'
                    : 'text-neutral-400 hover:text-white hover:bg-neutral-700/50'
                )}
              >
                <Mail className="w-4 h-4" />
                <span>Regular Templates</span>
                <span className="ml-2 text-xs bg-neutral-600 text-neutral-300 px-2 py-1 rounded-full">
                  {templates.filter(t => t.type === 'email' && t.author !== 'Beautiful Templates').length}
                </span>
                </button>
              </div>
          )}

          {/* Search and Category Filter */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 md:space-x-6">
            {/* Search Bar */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-neutral-400" />
                  <input
                type="text"
                  value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`Search ${tabs.find(t => t.id === activeTab)?.label.toLowerCase()} templates...`}
                className="w-full pl-10 pr-4 py-3 bg-neutral-800/50 border border-neutral-700 rounded-xl text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-transparent transition-all"
                  />
                </div>

            {/* Category Filter */}
            <div className="flex items-center space-x-1 bg-neutral-800/50 rounded-xl p-1">
              {['All', 'Cold Outreach', 'Follow-up', 'Meeting Request', 'Proposal', 'Thank You'].map((category) => (
                <button
                  key={category}
                  onClick={() => setCategoryFilter(category)}
                  className={cn(
                    'px-3 py-2 text-xs font-medium rounded-lg transition-all duration-200 whitespace-nowrap',
                    categoryFilter === category
                      ? 'bg-purple-500 text-white shadow-lg'
                      : 'text-neutral-400 hover:text-white hover:bg-neutral-700/50'
                  )}
                >
                  {category}
                </button>
              ))}
              </div>
              </div>
        </motion.div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="mt-6"
          >
            {filteredTemplates.length === 0 && !(activeTab === 'email' && emailSubTab === 'prebuilt' && beautifulTemplatesForDisplay.length > 0) ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-neutral-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">
                  No {activeTab} templates found
                </h3>
                <p className="text-neutral-400 mb-6">
                  {searchQuery || categoryFilter !== 'All' 
                    ? 'Try adjusting your search or filters'
                    : `Create your first ${activeTab} template to get started`
                  }
                </p>
                <Button
                  onClick={handleCreateTemplate}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New {activeTab === 'email' ? 'Email' : activeTab === 'sms' ? 'SMS' : activeTab === 'linkedin' ? 'LinkedIn' : 'Call Script'} Template
                </Button>
              </div>
            ) : (
              <>
                {/* Enhanced Email Templates Section */}
                {activeTab === 'email' && emailSubTab === 'regular' && (
                  <div className="space-y-8 mb-8">
                    {/* AI Template Generator */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-2xl p-6"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                            <Brain className="w-4 h-4 text-white" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-white">AI Template Generator</h3>
                            <p className="text-purple-200 text-sm">Create high-converting email templates with AI</p>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                        >
                          <Zap className="w-4 h-4 mr-2" />
                          Generate Template
                        </Button>
            </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {[
                          { title: 'Smart Subject Lines', desc: 'AI-powered subject line optimization', metric: '+24% open rate' },
                          { title: 'Dynamic Content', desc: 'Personalized content for each recipient', metric: '+18% engagement' },
                          { title: 'A/B Testing', desc: 'Automated testing and optimization', metric: '+31% conversions' }
                        ].map((feature, index) => (
                          <div key={index} className="p-4 bg-neutral-800/30 rounded-xl">
                            <h4 className="text-white font-medium text-sm mb-1">{feature.title}</h4>
                            <p className="text-neutral-300 text-xs mb-2">{feature.desc}</p>
                            <span className="text-green-400 text-xs font-medium">{feature.metric}</span>
                          </div>
                        ))}
                      </div>
                    </motion.div>

                    {/* Performance Insights */}
                <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 }}
                      className="bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl p-6 shadow-xl"
                    >
                      <div className="flex items-center justify-between mb-6">
                        <div>
                          <h3 className="text-lg font-semibold text-white">Template Performance Insights</h3>
                          <p className="text-neutral-400 text-sm">Real-time analytics for your email templates</p>
                        </div>
                        <Button variant="outline" size="sm">
                          <BarChart3 className="w-4 h-4 mr-2" />
                          View Analytics
                        </Button>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        {[
                          { label: 'Top Performer', template: 'Cold Outreach - SaaS', rate: '32.4%', type: 'open' },
                          { label: 'Best CTR', template: 'Follow-up Email', rate: '8.7%', type: 'click' },
                          { label: 'Highest Reply', template: 'Meeting Request', rate: '15.2%', type: 'reply' },
                          { label: 'Trending Up', template: 'Product Demo', rate: '+24%', type: 'growth' }
                        ].map((insight, index) => (
                          <div key={index} className="p-4 bg-neutral-800/30 rounded-xl">
                            <div className="text-neutral-400 text-xs mb-1">{insight.label}</div>
                            <div className="text-white font-medium text-sm mb-1 truncate">{insight.template}</div>
                            <div className={cn(
                              "text-xs font-medium",
                              insight.type === 'growth' ? 'text-green-400' :
                              insight.type === 'open' ? 'text-blue-400' :
                              insight.type === 'click' ? 'text-purple-400' :
                              'text-cyan-400'
                            )}>
                              {insight.rate}
                            </div>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                    
                    {/* Smart Recommendations */}
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                      className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border border-blue-500/20 rounded-2xl p-6"
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
                            <Target className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-white">Smart Recommendations</h3>
                            <p className="text-blue-200 text-sm">Optimize your templates for better performance</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        {[
                          { 
                            type: 'optimization', 
                            message: 'Consider adding urgency to "Cold Outreach - SaaS" subject line', 
                            impact: '+12% open rate',
                            action: 'Apply Fix'
                          },
                          { 
                            type: 'a-b-test', 
                            message: 'Test shorter vs longer content in "Follow-up Email"', 
                            impact: 'Unknown impact',
                            action: 'Start Test'
                          },
                          { 
                            type: 'personalization', 
                            message: 'Add industry-specific content to improve relevance', 
                            impact: '+8% reply rate',
                            action: 'Enhance'
                          }
                        ].map((rec, index) => (
                          <div key={index} className="flex items-center justify-between p-4 bg-neutral-800/30 rounded-xl">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-1">
                                <div className={cn(
                                  "w-2 h-2 rounded-full",
                                  rec.type === 'optimization' ? 'bg-green-400' :
                                  rec.type === 'a-b-test' ? 'bg-purple-400' :
                                  'bg-blue-400'
                                )}></div>
                                <span className="text-white text-sm font-medium">{rec.message}</span>
                              </div>
                              <span className="text-neutral-400 text-xs">{rec.impact}</span>
                            </div>
                            <Button variant="outline" size="sm" className="ml-4">
                              {rec.action}
                            </Button>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  </div>
                )}

                {/* Template Grid */}
                {emailSubTab === 'regular' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredTemplates.map((template, index) => (
                      <motion.div
                        key={template.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        whileHover={{ y: -4, scale: 1.02 }}
                        className="group relative bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl p-6 hover:border-neutral-600/50 transition-all duration-300 shadow-xl hover:shadow-2xl"
                      >
                        {/* Template Header */}
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <div className={cn(
                              "w-10 h-10 rounded-xl flex items-center justify-center",
                              template.type === 'email' ? 'bg-gradient-to-r from-blue-500 to-cyan-500' :
                              template.type === 'sms' ? 'bg-gradient-to-r from-green-500 to-emerald-500' :
                              template.type === 'linkedin' ? 'bg-gradient-to-r from-blue-600 to-indigo-600' :
                              'bg-gradient-to-r from-red-500 to-pink-500'
                            )}>
                              {template.type === 'email' && <Mail className="w-5 h-5 text-white" />}
                              {template.type === 'sms' && <MessageSquare className="w-5 h-5 text-white" />}
                              {template.type === 'linkedin' && <Building2 className="w-5 h-5 text-white" />}
                              {template.type === 'call' && <Phone className="w-5 h-5 text-white" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-white text-sm truncate">{template.name}</h3>
                              <div className="flex items-center space-x-2 mt-1">
                                <span className={cn(
                                  "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium",
                                  template.category === 'cold-outreach' ? "bg-blue-500/20 text-blue-400" :
                                  template.category === 'follow-up' ? "bg-green-500/20 text-green-400" :
                                  template.category === 'meeting-request' ? "bg-purple-500/20 text-purple-400" :
                                  template.category === 'proposal' ? "bg-orange-500/20 text-orange-400" :
                                  "bg-pink-500/20 text-pink-400"
                                )}>
                                  {template.category.replace('-', ' ')}
                                </span>
                                {template.isActive && (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
                                    <div className="w-1.5 h-1.5 bg-green-400 rounded-full mr-1"></div>
                                    Active
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-1">
                            <button className="p-1.5 text-neutral-400 hover:text-white hover:bg-neutral-700/50 rounded-lg transition-colors">
                              <Star className="w-4 h-4" />
                </button>
                            <div className="relative">
                              <button className="p-1.5 text-neutral-400 hover:text-white hover:bg-neutral-700/50 rounded-lg transition-colors">
                                <MoreVertical className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* Template Content Preview */}
                        <div className="mb-4">
                          {template.subject && (
                            <div className="mb-2">
                              <span className="text-neutral-400 text-xs">Subject:</span>
                              <p className="text-white text-sm font-medium truncate">{template.subject}</p>
                            </div>
                          )}
                          <div>
                            <span className="text-neutral-400 text-xs">Content:</span>
                            <p className="text-neutral-300 text-xs mt-1 line-clamp-3">{template.content}</p>
                          </div>
                        </div>

                        {/* Template Tags */}
                        <div className="flex flex-wrap gap-1 mb-4">
                          {template.tags.slice(0, 3).map((tag, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-neutral-700/50 text-neutral-300"
                            >
                              {tag}
                            </span>
                          ))}
                          {template.tags.length > 3 && (
                            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-neutral-700/50 text-neutral-400">
                              +{template.tags.length - 3}
                            </span>
                          )}
            </div>

                        {/* Template Performance Metrics */}
                        {(template.openRate || template.replyRate) && (
                          <div className="grid grid-cols-2 gap-4 mb-4 p-3 bg-neutral-800/30 rounded-xl">
                            {template.openRate && (
                              <div>
                                <div className="text-neutral-400 text-xs">Open Rate</div>
                                <div className="text-green-400 font-medium text-sm">{template.openRate}%</div>
                              </div>
                            )}
                            {template.replyRate && (
                              <div>
                                <div className="text-neutral-400 text-xs">Reply Rate</div>
                                <div className="text-blue-400 font-medium text-sm">{template.replyRate}%</div>
          </div>
        )}
                </div>
                        )}

                        {/* Template Footer */}
                        <div className="flex items-center justify-between text-xs text-neutral-400 mb-4">
                          <div className="flex items-center space-x-2">
                            <Users className="w-3 h-3" />
                            <span>{template.usage} uses</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Calendar className="w-3 h-3" />
                            <span>{new Date(template.updatedAt).toLocaleDateString()}</span>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 border-neutral-600 text-neutral-300 hover:bg-neutral-700"
                            onClick={() => handleView(template)}
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            Preview
                          </Button>
                          <Button
                            size="sm"
                            className="flex-1 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
                            onClick={() => handleEdit(template)}
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Edit
                          </Button>
                        </div>

                        {/* Enhanced Email Template Badge */}
                        {template.type === 'email' && template.openRate && template.openRate > 30 && (
                          <div className="absolute -top-2 -right-2">
                            <div className="w-6 h-6 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                              <Award className="w-3 h-3 text-white" />
                            </div>
                          </div>
                        )}

                        {/* Hover effect overlay */}
                        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 to-purple-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      </motion.div>
                    ))}
              </div>
            )}

                {/* Beautiful Templates Grid */}
                {activeTab === 'email' && emailSubTab === 'prebuilt' && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-8"
                  >
                    {/* Beautiful Templates Header */}
                    <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-2xl p-6 mb-6">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                            <Sparkles className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="text-2xl font-bold text-white">Beautiful Email Templates</h3>
                            <p className="text-purple-200">Professional, responsive designs with AI personalization</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <Button
                            onClick={() => setShowPersonalizationModal(true)}
                            variant="outline"
                            className="border-purple-500/50 text-purple-400"
                          >
                            <User className="w-4 h-4 mr-2" />
                            Add Recipient
                          </Button>
                          <Button
                            onClick={() => setShowTemplateGallery(true)}
                            className="bg-gradient-to-r from-purple-500 to-pink-500"
                          >
                            <Palette className="w-4 h-4 mr-2" />
                            Gallery View
                          </Button>
                        </div>
                      </div>
                      
                      {recipientData && (
                        <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-xl p-4">
                          <div className="flex items-center space-x-2 mb-2">
                            <CheckCircle className="w-5 h-5 text-green-400" />
                            <span className="text-green-400 font-medium">Personalization Ready</span>
                          </div>
                          <p className="text-neutral-300 text-sm">
                            Templates will be personalized for <strong>{recipientData.firstName} {recipientData.lastName}</strong>
                            {recipientData.company && ` from ${recipientData.company}`}
                          </p>
          </div>
        )}
                    </div>

                    {/* Beautiful Templates Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {beautifulTemplatesForDisplay.map((template, index) => (
                        <motion.div
                          key={template.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          whileHover={{ y: -4, scale: 1.02 }}
                          className="group relative bg-gradient-to-br from-neutral-900/80 to-neutral-800/50 backdrop-blur-xl border border-neutral-700/50 rounded-2xl p-6 hover:border-purple-500/50 transition-all duration-300 shadow-xl hover:shadow-2xl"
                        >
                          {/* Template Header */}
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center space-x-3">
                              <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center">
                                <Palette className="w-6 h-6 text-white" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-bold text-white text-lg truncate">{template.name}</h3>
                                <div className="flex items-center space-x-2 mt-1">
                                  <span className={cn(
                                    "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium",
                                    template.category === 'sales' ? "bg-blue-500/20 text-blue-400" :
                                    template.category === 'followup' ? "bg-green-500/20 text-green-400" :
                                    template.category === 'calendar' ? "bg-purple-500/20 text-purple-400" :
                                    "bg-orange-500/20 text-orange-400"
                                  )}>
                                    {template.category}
                                  </span>
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-400">
                                    <Sparkles className="w-3 h-3 mr-1" />
                                    AI Ready
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-1">
                <button
                                onClick={() => handlePreviewBeautifulTemplate(template)}
                                className="p-2 text-neutral-400 hover:text-purple-400 hover:bg-purple-500/10 rounded-lg transition-colors"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button className="p-2 text-neutral-400 hover:text-yellow-400 hover:bg-yellow-500/10 rounded-lg transition-colors">
                                <Star className="w-4 h-4" />
                </button>
                    </div>
                </div>

                          {/* Template Description */}
                          <div className="mb-4">
                            <p className="text-neutral-300 text-sm leading-relaxed">{template.description}</p>
                          </div>

                          {/* Template Features */}
                          <div className="flex flex-wrap gap-1 mb-4">
                            {template.features.slice(0, 3).map((feature, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-gradient-to-r from-purple-500/10 to-pink-500/10 text-purple-300 border border-purple-500/20"
                              >
                                {feature}
                              </span>
                            ))}
                            {template.features.length > 3 && (
                              <span className="inline-flex items-center px-2 py-1 rounded-md text-xs bg-neutral-700/50 text-neutral-400">
                                +{template.features.length - 3} more
                              </span>
                            )}
                          </div>

                          {/* Template Themes */}
                          {template.themes.length > 0 && (
                            <div className="mb-4">
                              <div className="text-neutral-400 text-xs mb-2">Available Themes:</div>
                              <div className="flex space-x-2">
                                {template.themes.slice(0, 3).map((theme, index) => (
                                  <div
                                    key={index}
                                    className="w-6 h-6 rounded-full border-2 border-white/20"
                                    style={{ backgroundColor: theme.primaryColor }}
                                    title={theme.name}
                />
              ))}
                                {template.themes.length > 3 && (
                                  <div className="w-6 h-6 rounded-full bg-neutral-600 flex items-center justify-center text-xs text-white">
                                    +{template.themes.length - 3}
                </div>
              )}
            </div>
                  </div>
                )}

                          {/* Action Buttons */}
                          <div className="flex items-center space-x-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex-1 border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                              onClick={() => handlePreviewBeautifulTemplate(template)}
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              Preview
                            </Button>
                            <Button
                              size="sm"
                              className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                              onClick={() => handleBeautifulTemplateSelection(template)}
                            >
                              <Play className="w-4 h-4 mr-2" />
                              Use Template
                            </Button>
                    </div>

                          {/* Beautiful Template Badge */}
                          <div className="absolute -top-2 -right-2">
                            <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                              <Sparkles className="w-4 h-4 text-white" />
              </div>
                          </div>
                        </motion.div>
                      ))}
            </div>

                    {/* Preview Modal */}
                    {previewTemplate && selectedTemplatePreview && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                        onClick={() => {
                          setPreviewTemplate(null);
                          setSelectedTemplatePreview('');
                        }}
                      >
                        <motion.div
                          initial={{ scale: 0.95 }}
                          animate={{ scale: 1 }}
                          className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-4 text-white">
                            <div className="flex items-center justify-between">
                              <div>
                                <h3 className="text-lg font-bold">{previewTemplate.name} - Preview</h3>
                                <p className="text-purple-100 text-sm">
                                  {recipientData ? `Personalized for ${recipientData.firstName}` : 'Sample preview'}
                                </p>
                              </div>
                              <button
                                onClick={() => {
                                  setPreviewTemplate(null);
                                  setSelectedTemplatePreview('');
                                }}
                                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                              >
                                <X className="w-5 h-5" />
                              </button>
                            </div>
                          </div>
                          <div className="h-[calc(90vh-100px)] overflow-auto">
                            <iframe
                              srcDoc={selectedTemplatePreview}
                              className="w-full h-full border-none"
                              title="Template Preview"
                              sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-top-navigation"
            />
          </div>
                        </motion.div>
                      </motion.div>
                    )}
                  </motion.div>
                )}
              </>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Personalized Email Builder Modal */}
        <AnimatePresence>
          {showEmailBuilder && (
            <PersonalizedEmailBuilder
              isOpen={showEmailBuilder}
              onClose={() => {
                setShowEmailBuilder(false);
                setEditingTemplate(undefined);
              }}
              onSave={handleTemplateFromBuilder}
              existingTemplate={editingTemplate}
            />
          )}
        </AnimatePresence>

        {/* PersonalizationModal */}
        <PersonalizationModal
          isOpen={showPersonalizationModal}
          onClose={() => setShowPersonalizationModal(false)}
          onSave={handlePersonalizationSave}
          initialData={recipientData}
        />

        {/* TemplateGallery */}
        <TemplateGallery
          isOpen={showTemplateGallery}
          onClose={() => setShowTemplateGallery(false)}
          onSelectTemplate={handleBeautifulTemplateSelection}
          recipientData={recipientData}
          showPersonalization={!!recipientData}
        />
      </div>
    </div>
  );
};